/**
 * One row in the customer-history sidebar. Displays a single offer or
 * order; clicking the main link opens it in the shared orders/offers
 * detail popup, and an inline <select> lets the user change the status
 * without leaving the Sales Inbox.
 *
 * Status changes mirror the flow used in the order/offer detail popup
 * (orders/detail/body/order/status/statuses/view.js):
 *   1. model.updateStatus(newStatus)
 *   2. model.fetch() + model.fetchListData() → refresh trackings
 *   3. Look up the target status in `orders:statuses`; if it carries a
 *      `params` blob run OrdersMailsUtils.check([model], params)
 *   4. If the status requests `sendMail`, open the configured mail
 *      template via OrdersMailsUtils.sendMail([model], template)
 *
 * Marionette.ItemView over an app.ns.Order model (see entities/orders) —
 * the same model class the orders list, customer detail Offers/Orders tabs
 * and orders detail popup all use, so updateStatus() is available
 * directly.
 */
define('salesInboxCommandItemView',[
  'app',
  'module',
  'marionette',
  'underscore',
  'jquery',
  'require',
  'settings',
  'ordersMailsUtils',
  'secondScreenBridge'
], function (App, Module, Marionette, _, $, require, Settings, OrdersMailsUtils, SecondScreen) {
  'use strict';

  // Supplier / invoice templates have their own dedicated send flows (and
  // recipients) inside OrdersMailsUtils.sendMail, so they don't belong in a
  // "reply to the customer" picker — keep only customer-facing templates.
  var NON_CUSTOMER_TEMPLATES = {
    MAIL_ORDER_SUPPLIER: true,
    MAIL_ORDER_SUPPLIER_REQUEST: true,
    MAIL_ORDER_INVOICE: true
  };

  // Configured mail templates (Settings 'mails.templates') that are customer
  // facing, shaped for the row menu. Same list the board sends from.
  function customerTemplates() {
    return _.chain(Settings.configValue('mails.templates') || [])
      .filter(function (t) { return t && t.template && !NON_CUSTOMER_TEMPLATES[t.template]; })
      .map(function (t) {
        return { value: t.template, label: _.i18n('mails.template.' + t.template) };
      })
      .value();
  }

  Module.exports = Marionette.ItemView.extend({
    tagName: 'li',
    className: 'cmd-item',

    template: _.template(
      '<% if (secondScreen) { %>' +
      '  <button type="button" class="cmd-second-screen" title="Open on the second screen">' +
      '    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>' +
      '  </button>' +
      '<% } %>' +
      '<% if (selectable) { %>' +
      '  <label class="cmd-check" title="Select for a bulk action"><input type="checkbox" <%= selected ? "checked" : "" %>></label>' +
      '<% } %>' +
      '<a class="cmd-link" href="javascript:void(0)" data-poi="<%= purchaseOrderId %>" title="Open in popup">' +
      '  <div class="l1">' +
      '    <span class="poi"><%= purchaseOrderId %></span>' +
      '    <% if (supplierNumber) { %><span class="ext">· <%= supplierNumber %></span><% } %>' +
      '  </div>' +
      '  <div class="l2"><span class="name"><%= name || "—" %></span></div>' +
      '  <div class="l3">' +
      '    <span class="when"><%= createDate ? new Date(createDate).toLocaleDateString("fr-BE") : "—" %></span>' +
      '    <span class="total">€<%= Math.round(Number(price) || 0) %></span>' +
      '  </div>' +
      '</a>' +
      '<div class="cmd-status">' +
      '  <select class="status-select" title="Change status">' +
      '    <% if (!statuses || !statuses.length) { %>' +
      '      <option value="<%= status %>" selected><%= status || "—" %></option>' +
      '    <% } else { %>' +
      '      <% if (showUnknown) { %>' +
      '        <option value="<%= status %>" selected><%= status %></option>' +
      '      <% } %>' +
      '      <% for (var i = 0; i < statuses.length; i++) { var s = statuses[i]; %>' +
      '        <option value="<%= s.id %>" <%= status === s.id ? "selected" : "" %>><%= s.label %></option>' +
      '      <% } %>' +
      '    <% } %>' +
      '  </select>' +
      '  <% if (templates && templates.length) { %>' +
      '  <select class="cmd-tpl-select" title="Send a mail template for this order">' +
      '    <option value="">✉ Send template…</option>' +
      '    <% _.each(templates, function (t) { %>' +
      '      <option value="<%= t.value %>"><%= t.label %></option>' +
      '    <% }); %>' +
      '  </select>' +
      '  <% } %>' +
      '</div>'
    ),

    modelEvents: {
      'change:status': 'render'
    },

    initialize: function (options) {
      this.statuses = (options && options.statuses) || [];
      this.secondScreen = !!(options && options.secondScreen);
      // Sales Inbox host: a status that's configured to send a templated mail
      // loads it into the inbox reply composer (via this callback) instead of
      // opening the EmailView popup. Absent (e.g. customer-detail tabs) → the
      // status change keeps the default popup behaviour.
      this.onComposeTemplate = (options && options.onComposeTemplate) || null;
      // Multiselect: whether to show the row checkbox, the shared selected-id
      // map (owned by the parent list) driving its checked state, and the
      // callback that notifies the parent when this row is (de)selected.
      this.selectable = !!(options && options.selectable);
      this.selectedIds = (options && options.selectedIds) || {};
      this.onToggleSelectCb = (options && options.onToggleSelect) || null;
    },

    serializeData: function () {
      // Work off the app.ns.Order model's fields + helper methods so we
      // render identical text to the customer Offers/Orders tabs.
      var m = this.model;
      var status = m.get('status') || '';
      // If the current status isn't in the fetched list (rare — e.g. a
      // legacy/archived status no longer offered), surface it as a
      // first option so the user still sees what it is.
      var showUnknown = false;
      if (status && this.statuses && this.statuses.length) {
        showUnknown = !_.some(this.statuses, function (s) { return s.id === status; });
      }
      return {
        purchaseOrderId: m.get('purchaseOrderId'),
        name: typeof m.getProductName === 'function' ? m.getProductName() : (m.get('productName') || m.get('productBaseCode') || ''),
        supplierNumber: m.get('supplierNumber') || '',
        createDate: m.get('createDate') || m.get('date'),
        price: m.get('price'),
        status: status,
        statuses: this.statuses,
        showUnknown: showUnknown,
        secondScreen: this.secondScreen,
        selectable: this.selectable,
        selected: !!this.selectedIds[m.get('purchaseOrderId')],
        // Only offer the template picker when this host wired up the composer
        // sink (the Sales Inbox) — elsewhere (customer-detail tabs) there's no
        // reply composer to load it into.
        templates: this.onComposeTemplate ? customerTemplates() : []
      };
    },

    events: {
      'click .cmd-link': 'onOpen',
      'click .cmd-second-screen': 'onOpenSecondScreen',
      // Any click inside the status control should stay on the control,
      // not propagate up and trigger the row's open-in-popup action.
      'click .cmd-status': 'onStopPropagation',
      'click .cmd-check': 'onStopPropagation',
      'change .cmd-check input': 'onToggleSelect',
      'change .status-select': 'onStatusChange',
      'change .cmd-tpl-select': 'onPickTemplate'
    },

    // Row checkbox toggled — tell the parent list to add/remove this order
    // from the current selection (the parent owns the selected-id map).
    onToggleSelect: function (e) {
      e.stopPropagation();
      var checked = $(e.currentTarget).is(':checked');
      if (this.onToggleSelectCb) { this.onToggleSelectCb(this.model, checked); }
    },

    // Push this offer/order to the marked second-screen tab (advanced mode).
    onOpenSecondScreen: function (e) {
      e.preventDefault();
      e.stopPropagation();
      SecondScreen.openCommand(this.model.get('purchaseOrderId'));
    },

    onOpen: function (e) {
      e.preventDefault();
      var poi = this.model.get('purchaseOrderId');
      if (!poi) { return; }
      // Reuse the existing orders detail popup — it dispatches by
      // commandType, so works for both offers and orders.
      require(['ordersDetailView'], function (DetailView) {
        if (DetailView && DetailView.prototype && DetailView.prototype.open) {
          DetailView.prototype.open(poi);
        }
      });
    },

    onStopPropagation: function (e) {
      e.stopPropagation();
    },

    // Replicates the status-change flow from the order/offer detail popup
    // (modules/pages/orders/detail/body/order/status/statuses/view.js) so
    // side-effects — mail confirmation popups, auto-sent templates, tracking
    // refresh — all fire the same way whether the user changes the status
    // from the popup or from this sidebar.
    onStatusChange: function (e) {
      e.stopPropagation();

      var model = this.model;
      if (!model.get('purchaseOrderId')) { return; }

      var $select = $(e.currentTarget);
      var newStatus = $select.val();
      var previous = model.get('status');
      if (newStatus === previous) { return; }

      var self = this;
      this.$el.addClass('status-saving');

      model.updateStatus(newStatus)
        .done(function () {
          // Refresh the underlying Order — shared via Backbone.Relational,
          // so any popup / list view already rendering this order updates
          // in place. Mirrors `this.model.fetch()` + `fetchListData()` from
          // the order-detail status view.
          model.fetch();

          model.fetchListData().done(function (data) {
            var listData = App.request('order:parse-list-data', data);
            model.set('trackings', listData.trackings);

            try {
              App.request('orders:statuses', model.get('commandType')).done(function (statuses) {
                var statusModel = null;
                _.each(statuses.models, function (item) {
                  if (item.get('statusConst') === newStatus) {
                    statusModel = item;
                  }
                });

                if (statusModel && statusModel.get('params')) {
                  var params;
                  try { params = JSON.parse(statusModel.get('params')); }
                  catch (err) { params = null; }
                  if (!params) { return; }

                  OrdersMailsUtils.check([model], params).done(function () {
                    var type = params && params.type;
                    if (type !== 'sendMail') { return; }

                    var template = params.params && params.params.type;
                    if (!template) { return; }

                    var configTemplate = null;
                    _.each(Settings.configValue('mails.templates'), function (t) {
                      if (t.template === template) { configTemplate = t; }
                    });

                    if (configTemplate) {
                      self.sendTemplateMail(configTemplate);
                    }
                  });
                }
              });
            } catch (err) {
              console.log(err);
            }
          });
        })
        .fail(function () {
          // Revert the select if the server refused the change.
          $select.val(previous);
        })
        .always(function () {
          if (!self.isDestroyed) { self.$el.removeClass('status-saving'); }
        });
    },

    // Send a configured mail template for this order. In the Sales Inbox the
    // template is loaded into the inline reply composer (via onComposeTemplate)
    // with the same email options the EmailView popup would receive — including
    // the board's attachments (proforma / proofsheets). Falls back to the popup
    // when the host can't accept it (composer returns falsy) or isn't wired.
    sendTemplateMail: function (configTemplate) {
      var model = this.model;
      var composer = this.onComposeTemplate;
      if (composer) {
        OrdersMailsUtils.sendMail([model], configTemplate, null, null, function (emailOptions) {
          if (!composer(emailOptions)) {
            OrdersMailsUtils.sendMail([model], configTemplate);
          }
        });
      } else {
        OrdersMailsUtils.sendMail([model], configTemplate);
      }
    },

    // ---- per-row "send a specific template" picker ----------------------
    // Pick a configured template from the row's <select> and send it for this
    // order (resets the select so it reads as an action, not a sticky value).
    onPickTemplate: function (e) {
      e.stopPropagation();
      var $select = $(e.currentTarget);
      var template = $select.val();
      $select.val('');
      if (!template) { return; }

      var configTemplate = null;
      _.each(Settings.configValue('mails.templates'), function (t) {
        if (t.template === template) { configTemplate = t; }
      });
      if (configTemplate) { this.sendTemplateMail(configTemplate); }
    }
  });
});

